import { useIsICRAResearchAssistBranding } from "@needl-ai/common/branding/hooks";
import { PopoverTrigger } from "@needl-ai/common/components/ui/popover";
import { useSidebar } from "@needl-ai/common/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";
import { useFetchUserInfo } from "@needl-ai/common/hooks/useFetchUserInfo";
import { cn } from "@needl-ai/common/lib/utils";
import { isICRAResearchAssist } from "@needl-ai/common/utils/isEnv";
import { getTrialTimeRemaining } from "@needl-ai/common/utils/trial";

import { UserProfilePopup } from "./UserProfilePopup";

const UserProfileAvatar = ({ name }: { name: string }) => {
  const initials = name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

  return (
    <div className="w-8 h-8 min-w-8 rounded-full flex items-center justify-center font-medium text-sm bg-primary text-primary-foreground">
      {initials}
    </div>
  );
};

const UserProfileContent = ({
  name,
  email,
  isCollapsed,
}: UserProfileContentProps) => {
  const isICRAResearchAssistBranding = useIsICRAResearchAssistBranding();

  const profileTrigger = (
    <div
      className={cn(
        "rounded-lg transition-colors",
        isICRAResearchAssistBranding
          ? "cursor-default"
          : "cursor-pointer hover:bg-sidebar-accent",
        isCollapsed
          ? "w-12 h-12 flex items-center justify-center"
          : "flex items-center gap-3 p-2"
      )}
      data-testid="profile-button"
    >
      <UserProfileAvatar name={name} />
      {!isCollapsed && (
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-sidebar-foreground">
            {name}
          </div>
          {email && (
            <div className="text-xs truncate text-sidebar-foreground/60 mt-0.5">
              {email}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const trigger = isICRAResearchAssistBranding ? (
    <TooltipTrigger asChild>{profileTrigger}</TooltipTrigger>
  ) : (
    <UserProfilePopup name={name} email={email}>
      <PopoverTrigger asChild>
        <TooltipTrigger asChild>{profileTrigger}</TooltipTrigger>
      </PopoverTrigger>
    </UserProfilePopup>
  );

  return (
    <div className={cn(isCollapsed && "flex justify-center")}>
      <TooltipProvider>
        <Tooltip>
          {trigger}
          {isCollapsed && (
            <TooltipContent side="right">
              <div>
                <p className="font-medium">{name}</p>
                {email && (
                  <p className="text-xs text-muted-foreground">{email}</p>
                )}
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export const UserProfile = () => {
  const { state } = useSidebar();
  const { data: userData, isLoading, isError } = useFetchUserInfo();

  const isCollapsed = state === "collapsed";

  if (isLoading) {
    if (isCollapsed) {
      return (
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 p-2">
        <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-4 bg-muted rounded animate-pulse w-24" />
          <div className="h-3 bg-muted rounded animate-pulse w-32" />
        </div>
      </div>
    );
  }

  if (isError || !userData) {
    return <div className="text-sm text-red-500">Error loading user data</div>;
  }

  const { username, email_id, trial_expires_at } = userData;
  const trialText = trial_expires_at
    ? getTrialTimeRemaining(trial_expires_at, true)
    : null;

  return (
    <div className="flex flex-col gap-2">
      <UserProfileContent
        name={username}
        email={email_id}
        isCollapsed={isCollapsed}
      />
      {!isCollapsed && trialText && isICRAResearchAssist() && (
        <div className="px-2">
          <div className="text-xs text-center py-1.5 px-3 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
            Trial: {trialText} left
          </div>
        </div>
      )}
    </div>
  );
};

type UserProfileContentProps = {
  name: string;
  email?: string;
  isCollapsed: boolean;
};
